import { inject, Injectable } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { ApiFragment, Family } from '@stylehaven/components';
import { saveAs } from 'file-saver';
import { Mime } from 'mime';
import otherTypes from 'mime/types/other.js';
import standardTypes from 'mime/types/standard.js';
import { ReplaceUnsupportedCharacters } from '../exports/replace-unsupported-characters.helper';
import { SearchService } from '../api/search.service';

@Injectable({ providedIn: 'root' })
export class DownloadsService {
    private downloadName: string;
    private mime: Mime;

    // Injectors
    private readonly http: HttpClient = inject(HttpClient);
    private readonly recordService: SearchService = inject(SearchService);

    constructor() {
        this.mime = new Mime(standardTypes, otherTypes); // Modifying default mime is not allowed, so we mimic it
        this.mime.define({ 'image/jpeg': ['jpg'] }, true); // Override jpg mapping to use jpg instead of jpeg
    }

    public async downloadFragmentPreview(record: ApiFragment): Promise<void> {
        const url: string = record.getAccessPreviewUrl();

        const blob: Blob = await this.http.get<Blob>(url, { responseType: 'blob' as 'json' }).toPromise();
        this.downloadName = this.getDownloadTitle(record, blob.type);

        saveAs(blob, this.downloadName);
        this.registerDownloadEvent(record);
    }

    public registerDownloadEvent(record: ApiFragment): void {
        this.recordService.registerDownload(record.getFragmentId());
    }

    public downloadUrl(url: string) {
        const a: HTMLAnchorElement = document.createElement('a');
        a.href = url;
        a.download = this.downloadName;
        a.click();
    }

    private getDownloadTitle(record: ApiFragment, mimeType: string): string {
        const originalFilename: string = record.get(Family.Descriptive, 'OriginalFilename');
        const cleanFilename: string = ReplaceUnsupportedCharacters(originalFilename);
        const filenameWithoutExtension: string = cleanFilename.replace(/\.[^/.]+$/, '');
        const extension: string | null = this.mime.getExtension(mimeType);

        return `${filenameWithoutExtension}.${extension}`;
    }
}
